import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  Button,
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Collapse,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { useState, useEffect } from "react";
import clsx from "clsx";
import settings from "settings";
import { getCampaignLanding, signupCampaignLanding } from "./actions";
import GlobalLoadScreen from "../globalLoadScreen";
import ErrorComponent from "../error";

// Helper to merge className strings
const cx = (...classNames) => classNames.filter(Boolean).join(" ");

// Text renderer that handles all text-level formatting
const renderText = (leaf, i) => {
  let text = leaf.text;

  // Handle empty text nodes
  if (!text) return <span key={i}>&nbsp;</span>;

  // Apply text formatting
  if (leaf.bold) text = <strong key={i}>{text}</strong>;
  if (leaf.italic) text = <em key={i}>{text}</em>;
  if (leaf.underline) text = <u key={i}>{text}</u>;
  if (leaf.code) text = <code key={i}>{text}</code>;

  return text;
};

// Accordion component that manages its own state
const AccordionSection = ({ node, index, children }) => {
  const [isOpen, setIsOpen] = useState(node.isOpen);

  return (
    <Card key={index} className="mb-0">
      <CardHeader
        className="d-flex justify-content-between align-items-center"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: "pointer" }}
      >
        <div className="flex-grow-1">
          {children[0]} {/* Header content */}
        </div>
        <div className="accordion-indicator">
          {isOpen ? (
            <span className="me-2">−</span> // Minus sign for open state
          ) : (
            <span className="me-2">+</span> // Plus sign for closed state
          )}
        </div>
      </CardHeader>
      <Collapse isOpen={isOpen}>
        <CardBody>
          {children[1]} {/* Body content */}
        </CardBody>
      </Collapse>
    </Card>
  );
};

// Simple renderer for Slate nodes
const renderNode = (node, index) => {
  // Handle text alignment
  const style = {};
  if (node.align) {
    style.textAlign = node.align;
  }

  // First handle leaf text nodes
  if (node.text !== undefined) {
    return renderText(node, index);
  }

  // Then handle parent nodes with children
  const children = node.children?.map((child, i) => renderNode(child, i));

  switch (node.type) {
    case "heading-one":
      return (
        <h2 className="mb-3 mt-2 title" key={index} style={style}>
          {children}
        </h2>
      );
    case "heading-two":
      return (
        <h3 className="mb-3 mt-2 title" key={index} style={style}>
          {children}
        </h3>
      );
    case "heading-three":
      return (
        <h3 key={index} style={style}>
          {children}
        </h3>
      );
    case "bulleted-list":
      return (
        <ul key={index} style={style}>
          {children}
        </ul>
      );
    case "numbered-list":
      return (
        <ol key={index} style={style}>
          {children}
        </ol>
      );
    case "list-item":
      return (
        <li key={index} style={style}>
          {children}
        </li>
      );
    case "blockquote":
      return (
        <blockquote key={index} style={style}>
          {children}
        </blockquote>
      );
    case "accordion":
      return (
        <div key={index} className="accordion mb-3">
          {children}
        </div>
      );
    case "accordion-item":
      return (
        <AccordionSection node={node} index={index}>
          {children}
        </AccordionSection>
      );
    case "accordion-item-header":
      return <div key={index}>{children}</div>;
    case "accordion-item-body":
      return <div key={index}>{children}</div>;
    case "paragraph":
    default:
      return (
        <p
          key={index}
          style={style}
          className={cx(node.indent && `ps-${node.indent * 3}`, "mb-3")}
        >
          {children}
        </p>
      );
  }
};

// Brief tab content component
const BriefTabContent = ({
  isAuthenticated,
  landingId,
  brief,
  isAssignedToCampaign,
  signupCampaignLanding,
}) => {
  // const joinCampaign = () => {
  //   console.debug("joinCampaign");
  //   signupCampaignLanding(landingId);
  // };
  return (
    <div className="campaign-content mb-5">
      {brief?.map((node, i) => renderNode(node, i))}
    </div>
  );
};

// Timeline card component
const TimelineCard = ({ stages }) => {
  if (!stages || !stages?.length) return null;
  return (
    <Card className="mb-3">
      <CardBody>
        <h5 className="mb-3">Timeline</h5>
        <div className="timeline">
          {stages?.map((stage, i) => (
            <div key={i} className="timeline-item">
              {stage.date && (
                <small className="text-muted">
                  {new Date(stage.date).toLocaleDateString()}
                </small>
              )}
              <p className={i === stages.length - 1 ? "mb-0" : "mb-1"}>
                {stage.name}
              </p>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

// CTA card component
const JoinCTACard = ({
  isAuthenticated,
  landingId,
  campaignId,
  isAssignedToCampaign,
  signupCampaignLanding,
}) => {
  const joinCampaign = () => {
    console.debug("joinCampaign");
    signupCampaignLanding(landingId);
  };

  return (
    <Card className="bg-light">
      <CardBody className="text-center">
        {isAuthenticated && isAssignedToCampaign ? (
          <>
            <h5 className="mb-3 text-success">✓ Campaign Joined!</h5>
            <p className="text-muted mb-3">
              You&apos;re already participating in this campaign.
            </p>
          </>
        ) : (
          <>
            <h5 className="mb-3">Ready to Join?</h5>
            {isAuthenticated ? (
              <Button
                color="primary"
                onClick={joinCampaign}
                block
                className="mb-2"
              >
                Join Now
              </Button>
            ) : (
              <>
                <Button
                  color="primary"
                  tag={Link}
                  to={`/signup?token=${campaignId}`}
                  block
                >
                  Join Campaign
                </Button>
              </>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

// Overview tab content component
const OverviewTabContent = ({
  isAuthenticated,
  landingId,
  campaignId,
  overview,
  stages,
  isAssignedToCampaign,
  signupCampaignLanding,
}) => {
  return (
    <Row>
      {/* Main content area */}
      <Col md={8}>
        <div className="campaign-content pe-md-4">
          <h2 className="mb-3 mt-2 title">Overview</h2>
          {overview?.map((node, i) => renderNode(node, i))}
        </div>
      </Col>

      {/* Right sidebar */}
      <Col md={4}>
        <aside>
          <TimelineCard stages={stages} />
          <JoinCTACard
            isAuthenticated={isAuthenticated}
            landingId={landingId}
            campaignId={campaignId}
            isAssignedToCampaign={isAssignedToCampaign}
            signupCampaignLanding={signupCampaignLanding}
          />
        </aside>
      </Col>
    </Row>
  );
};

// FAQ tab content component
const FAQTabContent = ({ faq }) => {
  return <>{faq?.map((node, i) => renderNode(node, i))}</>;
};

const CampaignLanding = ({
  isAuthenticated,
  match,
  location,
  campaignLanding,
  loading,
  getCampaignLanding,
  signupCampaignLanding,
}) => {
  const { landingId } = match.params;
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    getCampaignLanding(landingId);
  }, [landingId, getCampaignLanding]);

  console.debug("campaignLanding", campaignLanding);

  if (loading) {
    return <GlobalLoadScreen />;
  }

  if (!campaignLanding && !loading) {
    return <ErrorComponent />;
  }

  return (
    <Container>
      <Row className="justify-content-center">
        <Col xs="3" md="2">
          <img
            src={settings.mainbrand}
            className="brand_logo mb-5"
            style={{ filter: "brightness(0)" }}
            alt="brand logo"
            width={"70%"}
          />
        </Col>
      </Row>
      <Row className="justify-content-center mt-2">
        <Col xs={10}>
          <div
            className="d-flex align-items-center mb-4"
            style={{ gap: "1rem" }}
          >
            {campaignLanding?.logoFileUrl && (
              <img
                src={campaignLanding.logoFileUrl}
                alt="Campaign logo"
                className="me-3 rounded"
                style={{
                  height: "5rem",
                  width: "5rem",
                  objectFit: "cover",
                }}
              />
            )}
            <h1 className="title mb-0">
              {campaignLanding?.campaignName?.name}
            </h1>
          </div>
        </Col>
        <Col md={10}>
          <div className="border-0">
            <Nav tabs className="mb-0">
              <NavItem>
                <NavLink
                  className={clsx({ active: activeTab === "overview" })}
                  onClick={() => setActiveTab("overview")}
                  style={{ cursor: "pointer" }}
                >
                  Overview
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={clsx({ active: activeTab === "brief" })}
                  onClick={() => setActiveTab("brief")}
                  style={{ cursor: "pointer" }}
                >
                  Brief
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={clsx({ active: activeTab === "faq" })}
                  onClick={() => setActiveTab("faq")}
                  style={{ cursor: "pointer" }}
                >
                  FAQ
                </NavLink>
              </NavItem>
            </Nav>
            <Card
              style={{
                borderTopLeftRadius: "0px",
                borderTopRightRadius: "0px",
                borderTop: "1px solid transparent",
              }}
            >
              <CardBody>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="overview">
                    <OverviewTabContent
                      isAuthenticated={isAuthenticated}
                      landingId={landingId}
                      campaignId={
                        campaignLanding?.campaignName?._id
                        || campaignLanding?.campaign_id
                      }
                      overview={campaignLanding?.overview}
                      stages={campaignLanding?.stages}
                      isAssignedToCampaign={
                        campaignLanding?.isAssignedToCampaign
                      }
                      signupCampaignLanding={signupCampaignLanding}
                    />
                  </TabPane>
                  <TabPane tabId="brief">
                    <BriefTabContent
                      isAuthenticated={isAuthenticated}
                      landingId={landingId}
                      brief={campaignLanding?.brief}
                      isAssignedToCampaign={
                        campaignLanding?.isAssignedToCampaign
                      }
                      campaignId={
                        campaignLanding?.campaignName?._id
                        || campaignLanding?.campaign_id
                      }
                      signupCampaignLanding={signupCampaignLanding}
                    />
                  </TabPane>
                  <TabPane tabId="faq">
                    <FAQTabContent faq={campaignLanding?.faq} />
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: !!state.login.loginUser,
  loading: state.campaigns.loading,
  campaignLanding: state.campaigns.campaignLanding,
});

export default connect(mapStateToProps, {
  getCampaignLanding,
  signupCampaignLanding,
})(CampaignLanding);
